import React, { useMemo } from 'react';
import { CheckCircle, AlertCircle, Lightbulb, TrendingUp, XCircle, MessageSquare, Activity } from 'lucide-react';
import { analyzeSpeech } from '../utils/scoring';

import { getUITranslation } from '../utils/translation';

interface FeedbackPanelProps {
  transcript: string | null;
  targetPrompt: string;
  targetLanguage?: string;
}

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ transcript, targetPrompt, targetLanguage = 'Spanish' }) => {
  if (!transcript) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 min-h-[300px]">
        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
          <Activity className="w-6 h-6 text-blue-500" />
        </div>
        <p className="font-medium text-slate-700 text-sm">{getUITranslation('readyForAnalysis', targetLanguage)}</p>
        <p className="text-xs mt-1 text-slate-500 text-center max-w-[180px]">
          {getUITranslation('recordYourSpeechToGetInstantFeedback', targetLanguage)}
        </p>
      </div>
    );
  }

  // Analysis Logic
  const analysis = useMemo(() => {
    return analyzeSpeech(transcript, targetPrompt);
  }, [transcript, targetPrompt]);

  return (
    <div className="bg-white rounded-2xl shadow-lg shadow-blue-900/5 border border-slate-100 overflow-hidden h-full flex flex-col">
      <div className="px-5 py-3 border-b border-slate-50 bg-gradient-to-r from-blue-50/50 to-white flex justify-between items-center">
        <div>
          <h3 className="font-bold text-slate-900 text-base font-display">{getUITranslation('performance', targetLanguage)}</h3>
          <p className="text-[10px] text-blue-500 font-medium uppercase tracking-wider">{getUITranslation('aiAssessment', targetLanguage)}</p>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-baseline gap-1">
            <span className={`text-3xl font-black tracking-tight ${
              analysis.score >= 80 ? 'text-emerald-600' : 
              analysis.score >= 60 ? 'text-blue-600' : 'text-amber-500'
            }`}>
              {analysis.score}
            </span>
            <span className="text-slate-300 font-medium text-xs">/100</span>
          </div>
        </div>
      </div>
      
      <div className="p-5 space-y-5 flex-1 overflow-y-auto">
        {/* Feedback Message */}
        <div className={`p-3 rounded-xl border ${
          analysis.score >= 80 ? 'bg-emerald-50 border-emerald-100' :
          analysis.score >= 60 ? 'bg-blue-50 border-blue-100' :
          'bg-amber-50 border-amber-100'
        }`}>
          <h4 className={`font-bold mb-0.5 text-sm ${analysis.feedbackColor}`}>{analysis.feedbackTitle}</h4>
          <p className="text-xs text-slate-600">{analysis.feedbackMsg}</p>
        </div>

        {/* Detailed Word Analysis */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">{getUITranslation('speechAnalysis', targetLanguage)}</h4>
            <div className="flex gap-2 text-[10px]">
              <span className="flex items-center gap-1 text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> {getUITranslation('correct', targetLanguage)}
              </span>
              <span className="flex items-center gap-1 text-slate-600">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> {getUITranslation('mistake', targetLanguage)}
              </span>
            </div>
          </div>
          
          <div className="p-4 bg-slate-50/50 rounded-xl border border-slate-100 leading-loose text-base font-medium text-slate-800">
            {analysis.wordAnalysis.map((item, idx) => {
              if (item.status === 'correct') {
                return (
                  <span key={idx} className="inline-block mr-1 px-1.5 py-0.5 rounded text-emerald-700 bg-emerald-100/50 border border-emerald-100/50">
                    {item.word}
                  </span>
                );
              } else if (item.status === 'extra') {
                return (
                  <span key={idx} className="inline-block mr-1 px-1.5 py-0.5 rounded text-red-600 bg-red-50 border border-red-100 decoration-wavy underline decoration-red-300" title="Extra word">
                    {item.word}
                  </span>
                );
              } else if (item.status === 'missing') {
                return (
                  <span key={idx} className="inline-block mr-1 px-1.5 py-0.5 rounded text-slate-400 bg-slate-100/50 line-through decoration-slate-300 opacity-70" title="Missing word">
                    {item.word}
                  </span>
                );
              } else {
                // Incorrect (Substitution)
                return (
                  <span key={idx} className="group relative inline-block mr-1">
                    <span className="px-1.5 py-0.5 rounded text-red-600 bg-red-50 border border-red-100 decoration-dotted underline decoration-red-300 cursor-help">
                      {item.word}
                    </span>
                    {item.expected && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        Expected: {item.expected}
                      </span>
                    )}
                  </span>
                );
              }
            })}
          </div>
        </div>

        {/* Original Transcript (Hidden if perfect, shown for debugging/reference) */}
        {analysis.score < 100 && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 mb-1 font-medium uppercase tracking-wider">Raw Transcript</p>
            <div className="flex gap-2 text-slate-600 text-xs italic">
              <MessageSquare className="w-3 h-3 flex-shrink-0 mt-0.5 opacity-50" />
              <p>"{transcript}"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
